import round from 'lodash/round';
import { Text } from '@cotera/client/app/components';
import { classNames } from '@cotera/client/app/components/utils';
import React, { useEffect, useRef, useState } from 'react';
import { COLOR_MAP } from './utils';
import { SvgBar } from './components';

export const Histogram: React.FC<{
  direction?: 'horizontal' | 'vertical';
  buckets: { count: number | null; start: number | null; end: number | null }[];
  onClick?: (min: number, max: number) => void;
  activeBuckets?: { min: number; max: number }[];
  height?: number;
}> = ({
  buckets: _buckets,
  direction = 'vertical',
  onClick,
  activeBuckets,
  height = 26,
}) => {
  const [containerWidth, setContainerWidth] = useState<number>(0);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const updateWidth = (): void => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    };

    updateWidth();

    const resizeObserver = new ResizeObserver(updateWidth);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  const buckets: ProcessedBucket[] = _buckets.map((b) => ({
    count: Number(b.count),
    start: Number(b.start),
    end: Number(b.end),
  }));

  const maxBucketValue: number = round(
    Math.max(...buckets.map((b) => b.end)),
    2
  );
  const minBucketValue: number = round(
    Math.min(...buckets.map((b) => b.start)),
    2
  );

  return (
    <div
      className={classNames(
        'flex w-full h-full',
        direction === 'horizontal' ? 'flex-row items-center' : 'flex-col'
      )}
    >
      <div
        ref={containerRef}
        className={classNames(
          'flex h-full grow rounded overflow-hidden w-full',
          direction === 'horizontal' ? '' : 'mb-1'
        )}
      >
        {containerWidth > 0 && (
          <SvgHistogramChart
            buckets={buckets}
            onClick={onClick}
            activeBuckets={activeBuckets}
            width={containerWidth}
            height={height}
          />
        )}
      </div>
      <div className="flex flex-row justify-between">
        <Text.Caption
          className={classNames(
            'flex flew-row text-xs',
            direction === 'horizontal' ? 'ml-1' : 'mb-1'
          )}
        >
          {direction === 'vertical' && 'Min: '}
          {minBucketValue} - {direction === 'vertical' && 'Max: '}
          {maxBucketValue}
        </Text.Caption>
      </div>
    </div>
  );
};

interface ProcessedBucket {
  count: number;
  start: number;
  end: number;
}

interface ActiveBucket {
  min: number;
  max: number;
}

interface SvgHistogramChartProps {
  buckets: ProcessedBucket[];
  onClick?: (min: number, max: number) => void;
  activeBuckets?: ActiveBucket[];
  width: number;
  height: number;
}

const SvgHistogramChart: React.FC<SvgHistogramChartProps> = ({
  buckets,
  onClick,
  activeBuckets,
  width,
  height,
}) => {
  const max: number = Math.max(...buckets.map((b) => b.count));
  const totalRows: number = buckets.reduce((acc, curr) => acc + curr.count, 0);
  const barWidth: number = width / buckets.length;
  const barSpacing: number = 1; // 1px spacing between bars
  const actualBarWidth: number = Math.max(barWidth - barSpacing, 1);

  return (
    <svg width={width} height={height} className="block">
      {buckets.map((bucket, i) => {
        const barHeight: number = (bucket.count / max) * height;
        const percent: number = (bucket.count / totalRows) * 100;
        const x: number = i * barWidth;
        const y: number = height - barHeight; // Start from bottom

        // Use a tolerance for floating-point precision issues (JavaScript float precision is ~15-17 digits)
        const TOLERANCE = 1e-6; // 0.000001 - appropriate for 6 decimal places
        const isActive: boolean =
          activeBuckets?.some(
            (b) =>
              bucket.start >= b.min - TOLERANCE &&
              bucket.end <= b.max + TOLERANCE
          ) ?? false;

        return (
          <SvgBar
            key={i}
            x={x}
            y={y}
            color={isActive ? COLOR_MAP['active'] : COLOR_MAP['category']}
            width={actualBarWidth}
            height={barHeight}
            category={`${round(bucket.start, 2)} - ${round(bucket.end, 2)}`}
            rows={bucket.count}
            percent={percent}
            onClick={() =>
              onClick && onClick(round(bucket.start, 2), round(bucket.end, 2))
            }
            isActive={isActive}
          />
        );
      })}
    </svg>
  );
};
